import { ModalConfig } from './../controls/modal/modal-config';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { GeneralService } from 'src/app/service/general.service';
import { AuthenticationConfig, AccountPolicy } from './../../model/AuthResponse';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthenticationService } from 'src/app/service/authentication.service';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { Modal } from '../controls/modal/modal.service';
import { ModalMessageComponent, ModalMessageType } from '../controls/modal-message/modal-message.component';
import { EventService } from 'src/app/service/event.service';
import { Utils } from 'src/app/utils';
import { Events, WebsocketService } from 'src/app/service/websocket.service';
import { UserInfo, UserRole } from 'src/app/model/User';
import { of, Subscription } from 'rxjs';
import { emptyValidator } from 'src/app/validator/empty-form-validator';

@Component({
    selector: 'app-settings-authentication',
    templateUrl: './settings-authentication.component.html',
    styleUrls: ['../settings/settings.component.css', './settings-authentication.component.css'],
    standalone: false
})
export class SettingsAuthenticationComponent implements OnInit {

  isLoading:boolean = false;
  isSaving:boolean = false;
  canEdit:boolean = true;
  savingBtn:string = $localize`Save`;
  authConfig:AuthenticationConfig = new AuthenticationConfig();
  accountPolicy:AccountPolicy;
  OTDSUserGroups:string[] = [];
  currentUserAuthInfo : UserInfo;
  isOTDSAdminGroupSelectionNeeded:boolean = false;

  settingsForm = new FormGroup({
    authenticationMethodCtrl: new FormControl('', Validators.required),
    otdsURLCtrl: new FormControl(),
    otdsResourceIdCtrl: new FormControl(),
    otdsUserGroupCtrl: new FormControl(),
    accountLockDurationCtrl: new FormControl(),
    accountLockoutThresholdCtrl: new FormControl()
  });
  
  constructor(private authService: AuthenticationService,
              private genService: GeneralService,
              private modal: Modal,
              private eventService: EventService,
              private wsService: WebsocketService) { }

  get authenticationMethodCtrl() { return this.settingsForm.get('authenticationMethodCtrl'); }
  get otdsURLCtrl() { return this.settingsForm.get('otdsURLCtrl'); }
  get otdsResourceIdCtrl() { return this.settingsForm.get('otdsResourceIdCtrl'); }
  get otdsUserGroupCtrl() { return this.settingsForm.get('otdsUserGroupCtrl')}
  get accountLockDurationCtrl() { return this.settingsForm.get('accountLockDurationCtrl')}
  get accountLockoutThresholdCtrl() { return this.settingsForm.get('accountLockoutThresholdCtrl')}

  ngOnInit(): void {
    this.currentUserAuthInfo = this.authService.getLoggedInUser();
    this.canEdit = this.currentUserAuthInfo.role === UserRole.SUPERUSER;
    this.initData();
  }

  canDeactivate() : boolean{
    return !this.settingsForm.dirty &&
           !this.isOTDSFieldIncomplete();
  }

  isOTDSFieldIncomplete() : boolean {
    return this.authenticationMethodCtrl.value == 'otds' &&
           (this.otdsURLCtrl.value == '' || this.otdsResourceIdCtrl.value == '' || this.otdsUserGroupCtrl.value == '');
  }

  getDeactivateModalMessageConfig(): ModalConfig {
    let deactivationModalMsg: ModalConfig;

    if(this.isOTDSFieldIncomplete()){
      deactivationModalMsg = {
          data: {
                  title: $localize`Incomplete Directory Services configuration`,
                  message: $localize`Directory Services configuration is incomplete and this may prevent users from signing in. Are you sure you want to leave this page?`,
                  primaryBtn: $localize`Stay on this Page`,
                  secondaryBtn: $localize`Leave this Page`,
                  width: '450px',
                  type: ModalMessageType.Warning,
                },
          id: 'otsc-settings-auth-incomplete-otds',
          closeOnESCKeyUp: false
        }
    }else if(this.settingsForm.dirty){
      deactivationModalMsg = Utils.getDefaultDeactivateModalMessageConfig('otsc-settings-auth-unsaved-changes');
    }

    return deactivationModalMsg;
  }

  initData(showLoading:boolean = true):void {
    this.settingsForm.reset();
    if(showLoading){
      this.isLoading = true;
    }

    this.authService.getAuthenticationConfig().pipe(tap(resp => {
      this.authConfig.adminRoleGroup = resp.body.adminRoleGroup ? resp.body.adminRoleGroup : undefined;
      this.authConfig.authenticationMethod = resp.body.authenticationMethod;
      this.authConfig.otdsResourceId = resp.body.otdsResourceId ? resp.body.otdsResourceId : undefined;
      this.authConfig.otdsServerUrl = resp.body.otdsServerUrl ? resp.body.otdsServerUrl : undefined;
      this.authConfig.Administrator = resp.body.Administrator ? resp.body.Administrator : undefined;

      //subscribe to changes
      this.authenticationMethodCtrl.valueChanges.subscribe(value => {
        if(value != null){
          if(value == 'local') {
            //set local validators
            this.accountLockDurationCtrl.setValidators([Validators.required, Validators.min(1), Validators.max(60)]);
            this.accountLockoutThresholdCtrl.setValidators([Validators.required, Validators.min(1), Validators.max(10)]);          
  
            //clear OTDS validators
            this.otdsURLCtrl.clearValidators();
            this.otdsResourceIdCtrl.clearValidators();
            this.otdsUserGroupCtrl.clearValidators();
          }else if (value == 'otds') {
            //set OTDS validators
            this.otdsURLCtrl.setValidators([Validators.required, Validators.pattern('^(http|https)://.+')]);
            this.otdsResourceIdCtrl.setValidators([Validators.required, emptyValidator()]);
            this.otdsUserGroupCtrl.setValidators([Validators.required]);
            //clear local validators
            this.accountLockDurationCtrl.clearValidators();
            this.accountLockoutThresholdCtrl.clearValidators();
            this.otdsUserGroupCtrl.setValue(this.authConfig.adminRoleGroup ? this.authConfig.adminRoleGroup : '');
          }
  
          this.accountLockDurationCtrl.updateValueAndValidity();
          this.accountLockoutThresholdCtrl.updateValueAndValidity();
          this.otdsURLCtrl.updateValueAndValidity();
          this.otdsResourceIdCtrl.updateValueAndValidity();
          this.otdsUserGroupCtrl.updateValueAndValidity();
        }
      });

      this.otdsURLCtrl.valueChanges.subscribe(value => {
        this.checkIfOTDSAdminGroupIsSelected();
      });
      this.otdsResourceIdCtrl.valueChanges.subscribe(value => {
        this.checkIfOTDSAdminGroupIsSelected();
      });
      this.otdsUserGroupCtrl.valueChanges.subscribe(value => {
        this.checkIfOTDSAdminGroupIsSelected();
      });

      //set values
      if(this.authConfig.authenticationMethod == 'otds'){
        if(this.authConfig.Administrator && this.authConfig.Administrator != ''){
          this.authConfig.adminRoleGroup = this.authConfig.Administrator;
          this.OTDSUserGroups.push(this.authConfig.Administrator);
        }

        if(this.canEdit){
          this.getOTDSGroups();
        }
      }else{
        this.OTDSUserGroups = [];
        this.otdsUserGroupCtrl.setValue('');
      }
      this.authenticationMethodCtrl.setValue(this.authConfig.authenticationMethod);
      this.otdsURLCtrl.setValue(this.authConfig.otdsServerUrl);
      this.otdsResourceIdCtrl.setValue(this.authConfig.otdsResourceId);
      this.checkIfOTDSAdminGroupIsSelected();
    }),
    switchMap(() => this.genService.getAccountPolicy().pipe(
      tap(resp => {
      this.accountPolicy = resp.body;
      this.accountLockDurationCtrl.setValue(this.accountPolicy.lockoutDuration);
      this.accountLockoutThresholdCtrl.setValue(this.accountPolicy.lockoutThreshold);
      })
    )),
    finalize(() => {
      this.isLoading = false;
    })).subscribe();
  }

  save(): void {
    let isAccountPolicyChanged = this.authenticationMethodCtrl.value == 'local' && (this.accountLockDurationCtrl.dirty || this.accountLockoutThresholdCtrl.dirty);
    let msg:string = this.authConfig.authenticationMethod == 'local' && this.authenticationMethodCtrl.value == 'local' ?
                        $localize`The changes made on this page will be saved. Do you want to save? <br/>` :
                        $localize`The changes made on this page will be saved. Do you want to save? <br/><b>NOTE:</b> This change will sign out other users.`;
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Save`,
          message: msg,
          primaryBtn: $localize`Yes`,
          secondaryBtn: $localize`No`,
          width: '450px',
        },
        id: 'otsc-settings-auth-save'
      }
      ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          this.isSaving = true;
          this.savingBtn = $localize`Saving...`;
          this.authConfig.authenticationMethod = this.authenticationMethodCtrl.value;
          if (this.authenticationMethodCtrl.value == 'local') {
            this.authConfig.otdsServerUrl = '';
            this.authConfig.otdsResourceId = '';
            this.authConfig.adminRoleGroup = '';
            this.authConfig.Administrator = undefined;
          } else {
            this.authConfig.otdsServerUrl = this.otdsURLCtrl.value.trim();
            this.authConfig.otdsResourceId = this.otdsResourceIdCtrl.value.trim();
            this.authConfig.adminRoleGroup = this.otdsUserGroupCtrl.value;
          }

          if (this.canEdit) {
            this.authService.saveAuthenticationConfig(this.authConfig, this.wsService.from).pipe(tap(resp => {
              this.currentUserAuthInfo.settings.authenticationMethod = resp.body.authenticationMethod;
              this.authService.updateLoggedInUser(this.currentUserAuthInfo);

              if (!isAccountPolicyChanged) {
                this.initData();
                Utils.addHeaderMessageEventBanner(this.eventService, Events.SuccessMsg, $localize`Settings were saved successfully.`);
              }
            }),
              catchError((error: HttpErrorResponse) => {
                let msg: string = '';

                if (error.status === 405) {
                  msg = $localize`Directory services resource could not be activated. The specified resource is already activated.`;
                } else if (error.status === 404) {
                  msg = $localize`Directory services resource could not be activated. The specified resource identifier is invalid.`;
                } else {
                  msg = $localize`Settings could not be saved. Please try again later.`;
                }

                Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, msg);

                return of(error);
              }),
              finalize(() => {
                if (!isAccountPolicyChanged) {
                  this.isSaving = false;
                  this.savingBtn = $localize`Save`;
                }
              }),
            ).subscribe();
          }

          if (isAccountPolicyChanged) {
            this.accountPolicy.lockoutDuration = this.accountLockDurationCtrl.value;
            this.accountPolicy.lockoutThreshold = this.accountLockoutThresholdCtrl.value;

            this.genService.saveAccountPolicy(this.accountPolicy).pipe(tap(resp => {
              if (resp.body.completed && resp.body.success) {
                this.initData();
                Utils.addHeaderMessageEventBanner(this.eventService, Events.SuccessMsg, $localize`Settings were saved successfully.`);
              } else {
                Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, $localize`Settings could not be saved. Please try again later.`);
              }
            }),
              finalize(() => {
                this.isSaving = false;
                this.savingBtn = $localize`Save`;
              }),
            ).subscribe();
          }
        }
        sub.unsubscribe();
      });
  }

  reset(): void {
    const sub: Subscription =
      this.modal.open(ModalMessageComponent, {
        data: {
          title: $localize`Reset`,
          message: $localize`The changes made on this page will be lost. Do you want to reset?`,
          primaryBtn: $localize`Yes`,
          secondaryBtn: $localize`No`,
          width: '350px',
        },
        id: 'otsc-settings-auth-reset'
      }
      ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
        if (isPrimaryBtnClicked) {
          this.initData();
        }
        sub.unsubscribe();
      });
  }

  testOTDSURL(): void {
    this.authService.testOtdsConnection(this.otdsURLCtrl.value).pipe(tap(resp => {
      Utils.addHeaderMessageEventBanner(this.eventService, Events.SuccessMsg, $localize`Test connection succeeded.`);
    }),
    catchError((error) => {
      Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, $localize`Test connection failed. Please check OTDS configuration details and try again.`);
      return of(error);
    })).subscribe();
  }

  activateOTDSResource(): void {
    this.authService.activateOTDSResource(this.otdsURLCtrl.value, this.otdsResourceIdCtrl.value).pipe(tap(resp => {
      Utils.addHeaderMessageEventBanner(this.eventService, Events.SuccessMsg, $localize`Directory services resource was activated successfully. Please select administrators group and save your changes.`);
      this.OTDSUserGroups = [];
      resp.body.forEach(OTDSGroup => {
        this.OTDSUserGroups.push(OTDSGroup.id);
      });
      this.otdsUserGroupCtrl.setValue('');
    }),
    catchError((error:HttpErrorResponse) => {
      let msg:string = '';

      if(error.status === 405){
        msg = $localize`Directory services resource could not be activated. The specified resource is already activated.`;
      }else if(error.status === 404){
        msg = $localize`Directory services resource could not be activated. The specified resource identifier is invalid.`;
      }else if(error.status === 401){
        msg = $localize`Directory services resource could not be activated. The specified resource identifier is invalid.`;
      }else{
        msg = $localize`Directory services resource could not be activated. The system has failed to authenticate with the specified host.`;
      }

      Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, msg);
      return of(error);
    })).subscribe();
  }

  getOTDSGroups(): void {
    this.authService.getOtdsGroups().pipe(
      tap((resp) => {
        this.OTDSUserGroups = [];
        let isOTDSGroupFound:boolean = false;

        //get options
        resp.body.forEach(OTDSGroup => {
          this.OTDSUserGroups.push(OTDSGroup.id);
          //search by ID
          if(this.authConfig.adminRoleGroup == OTDSGroup.id){
            isOTDSGroupFound = true;
          }
        });

        if(!isOTDSGroupFound){
          //search by name for backwards compatibility
          resp.body.forEach(OTDSGroup => {
            if(this.authConfig.adminRoleGroup == OTDSGroup.name){
              isOTDSGroupFound = true;
              this.authConfig.adminRoleGroup = OTDSGroup.id;
            }
          });
        }
        this.otdsUserGroupCtrl.setValue(this.authConfig.adminRoleGroup ? this.authConfig.adminRoleGroup : '');
      }),
      catchError((error:HttpErrorResponse) => {
        Utils.addHeaderMessageEventBanner(this.eventService, Events.ErrorMsg, $localize`The system could not get directory services administrators groups.`);
        return of(error);
      }),
      finalize(() => {
        this.isLoading = false;
      })
    ).subscribe();
  }

  checkIfOTDSAdminGroupIsSelected(): void {
    if(this.authenticationMethodCtrl.value == 'otds' && 
          (this.otdsURLCtrl.valid && this.otdsResourceIdCtrl.valid && this.otdsUserGroupCtrl.invalid)){
      this.isOTDSAdminGroupSelectionNeeded = true;
    }else{
      this.isOTDSAdminGroupSelectionNeeded = false;
    }
  }
}
